import type { CSSProperties } from 'react';

import { nonNegativePixelValue, token } from './tokens';
import { isHexColor, HEX_COLOR_PATTERN } from './hex-color';

/**
 * The storefront's own hairline border color, already used site-wide (see
 * `--dcm-color-border` in `theme/core-menu-variables.ts` and the header's own
 * `border-bottom: 1px solid #c5d0d7` in `store-front-header.tsx`). Exposed as the
 * default "Theme" role so an author can reproduce that exact hairline without
 * hardcoding a hex that would silently drift from a future rebrand.
 */
const THEME_BORDER_COLOR = token('--dcm-color-border', '#c8d2db');

/**
 * A discriminated union rather than an ambiguous "role key or literal hex" string. This field is
 * brand new -- no page has ever stored a border, so there is no legacy shape to disambiguate at
 * read time, unlike `background.color`/`textColor` elsewhere in this directory. Modeling it as a
 * proper union instead of copying that older pattern gets a real closed enum in the AI schema (see
 * `BORDER_COLOR_AI_SCHEMA`) and removes the need for an `isXRole()`-style type guard entirely.
 */
export type BorderColor = { style: 'custom'; hex: string } | { style: 'none' } | { style: 'theme' };

export type BorderValue = {
  color: BorderColor;
  /** CSS pixel width, e.g. '1px'. Irrelevant while color.style is 'none'. */
  width: string;
};

/**
 * No border by default. `color: 'none'` is what guarantees that -- `borderStyle` short-circuits on
 * it and emits no declaration at all, whatever the width says.
 *
 * The width is therefore deliberately `1px`, not `0px`, and that is not a contradiction: it is the
 * width the border takes *once the author picks a colour*, since `BorderColorField` writes only
 * `color` and never touches `width`. At `0px` a freshly-picked colour would render
 * `border: 0px solid <colour>` -- invisible -- so choosing a colour would silently appear to do
 * nothing until the author also found and raised the width. `1px` makes the single action the
 * author took produce the hairline they asked for.
 */
export const DEFAULT_BORDER: BorderValue = { color: { style: 'none' }, width: '1px' };

/**
 * Resolves a `BorderColor` to a CSS color, or `undefined` if it cannot be resolved.
 *
 * The `default` branch is unreachable through the type system -- `_exhaustive: never` makes
 * TypeScript enforce that every `BorderColor` variant is handled above it, so adding a new style
 * without updating this function fails the build. At runtime it is not unreachable, though: saved
 * page data is unvalidated JSON an AI plugin or a hand-edit can author, so a value that violates
 * the type can still arrive here. It resolves to "no border" rather than throwing, matching every
 * other resolver in this directory -- a malformed value must never crash the surrounding page.
 */
function resolveBorderColor(color: BorderColor): string | undefined {
  switch (color.style) {
    case 'none':
      return undefined;
    case 'theme':
      return THEME_BORDER_COLOR;
    case 'custom':
      return isHexColor(color.hex) ? color.hex : undefined;
    default: {
      // Exists for its compile-time effect only: TypeScript rejects this assignment if a
      // BorderColor variant is ever added without a case above handling it. The runtime return
      // below is deliberately not `_exhaustive` itself -- returning the unrecognized value would
      // stringify it into the CSS declaration.
      const _exhaustive: never = color;
      return undefined;
    }
  }
}

/**
 * Converts a `BorderValue` into a React inline style.
 *
 * Accepts `undefined` for the exact same reason `resolveBorderColor` below tolerates a `style`
 * outside its union, and explicitly **not** as a backwards-compatibility concession: persisted page
 * data is unvalidated JSON, so a value violating the declared type can arrive here at runtime, and
 * such a value must never crash the surrounding page. An absent `border` key is that identical
 * mechanism -- a missing object rather than a malformed one -- so guarding the malformed case while
 * leaving this one to throw would be an inconsistency, not a policy.
 *
 * This is load-bearing rather than theoretical. `Section`/`FlexLayout` predate this field
 * (#21596/#21708/#21709) and Puck merges `defaultProps` into saved props in the *editor*
 * (`@puckeditor/core` dist/index.js:10767) but **not** on the production `<Render>` path
 * (`DropZoneRenderItem` passes `item.props` straight through) -- so an already-published page
 * reaches this function with `border` genuinely absent. There is no error boundary on either
 * renderer, so dereferencing it would white-screen the whole page, and because the editor *does*
 * inject the default the page would still look correct in the editor -- precisely the asymmetry
 * `section.tsx`'s padding comment already warns about ("fired on a live page yet was masked in the
 * editor by the injected default").
 *
 * Note this deliberately does *not* reintroduce the optional-prop/`?? DEFAULT_BORDER` machinery:
 * `border` stays required on `SectionProps`/`FlexLayoutProps`, so authoring code still cannot omit
 * it, and the AI schema stays a closed enum. This guard covers only what unvalidated stored data
 * can contain, which is a different question from what the type permits callers to pass.
 */
export function borderStyle(border: BorderValue | undefined): CSSProperties {
  if (!border) return {};

  const resolvedColor = resolveBorderColor(border.color);
  if (!resolvedColor) return {};

  const resolvedWidth = nonNegativePixelValue(border.width, DEFAULT_BORDER.width);

  return { border: `${resolvedWidth} solid ${resolvedColor}` };
}

/**
 * AI schema for `BorderValue.color`. A real closed enum for `style`, unlike the free-string
 * `description`-only schemas `BACKGROUND_AI_SCHEMA`/`CONTAINER_TEXT_COLOR_AI_SCHEMA` use elsewhere
 * in this directory -- those exist as loose strings specifically because they have to accept
 * either a role key or a raw hex with no way to tell the AI plugin which, a constraint this field
 * doesn't inherit because it was never an ambiguous string to begin with.
 */
export const BORDER_COLOR_AI_SCHEMA = {
  type: 'object',
  required: ['style'],
  properties: {
    style: {
      type: 'string',
      enum: ['none', 'theme', 'custom'],
      description: "Prefer 'theme' or 'none' so the border follows merchant branding.",
    },
    hex: {
      type: 'string',
      pattern: HEX_COLOR_PATTERN,
      description: "Only present when style is 'custom'.",
    },
  },
} as const;
